import { Button, Typography } from "antd";
import { CalendarOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import CompanyContacts from "components/EditCompanyContacts/CompanyContacts";
import { BookingProblem, BookingProblemCode, CONTACT_PROBLEMS } from "../bookingRequest";

const { Text, Title } = Typography;

const CONTACT_EMAIL = <a href="mailto:bedrift@bindeleddet.no">bedrift@bindeleddet.no</a>;

const HOW_TO_FIX: Record<BookingProblemCode, React.ReactNode> = {
  not_open: <>Vent til bookingen åpner, og prøv igjen.</>,
  booking_closed: <>Send en e-post til {CONTACT_EMAIL} for å booke.</>,
  not_company: <>Logg inn med bedriftsbrukeren deres for å kunne booke.</>,
  not_approved: (
    <>
      Har dere nylig opprettet bedriftsbrukeren, er Bindeleddet i gang med å se over og godkjenne bedriften. Har det
      gått en stund, eller har dere spørsmål, send en mail til {CONTACT_EMAIL}.
    </>
  ),
  already_booked: (
    <>
      Dere kan ha én bedriftspresentasjon per sesong. Ønsker dere å endre datoen eller avtale et ekstra arrangement, ta
      kontakt på {CONTACT_EMAIL}.
    </>
  ),
  no_main_contact: (
    <>
      Velg eller opprett en kontaktperson under. Dere kan også gjøre det i bookingen etter at dere har valgt dato, men
      gjør det gjerne nå, så går bookingen raskere når den åpner.
    </>
  ),
  main_contact_no_email: (
    <>
      Legg inn en e-postadresse på kontaktpersonen under – bekreftelsen på bookingen sendes dit. Dere kan også gjøre det
      i bookingen, men gjør det gjerne nå.
    </>
  ),
};

interface Props {
  problems: BookingProblem[];
  companyId: number | undefined;
  onRecheck: () => void;
}

/**
 * Lists what prevents the company from booking, with instructions on how to fix each problem.
 */
const BookingProblems: React.FC<Props> = ({ problems, companyId, onRecheck }) => {
  const [contactsOpen, setContactsOpen] = useState(false);
  const hasContactProblem = problems.some(problem => CONTACT_PROBLEMS.includes(problem.code));

  return (
    <div style={{ textAlign: "left" }}>
      <Title level={5}>Dette må ordnes før dere kan booke</Title>
      {problems.map(problem => (
        <div key={problem.code} style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
          <ExclamationCircleOutlined style={{ color: "#faad14", fontSize: "18px", marginTop: "3px" }} />
          <div>
            <Text strong>{problem.message}</Text>
            <br />
            <Text>{HOW_TO_FIX[problem.code]}</Text>
            {problem.event_id !== undefined && (
              <div style={{ marginTop: "8px" }}>
                <Link to={`/events/${problem.event_id}/`}>
                  <Button icon={<CalendarOutlined />}>Gå til arrangementet</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      ))}
      {hasContactProblem && companyId !== undefined && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          <Button type="primary" onClick={() => setContactsOpen(open => !open)}>
            {contactsOpen ? "Skjul kontaktpersoner" : "Administrer kontaktpersoner"}
          </Button>
          {/* Changing the main contact rechecks automatically, but editing a contact (e.g. adding the missing
              e-mail address) doesn't */}
          {contactsOpen && <Button onClick={onRecheck}>Sjekk på nytt</Button>}
        </div>
      )}
      {contactsOpen && companyId !== undefined && (
        <div style={{ marginTop: "20px" }}>
          <CompanyContacts companyId={companyId} onNewMain={onRecheck} />
        </div>
      )}
    </div>
  );
};

export default BookingProblems;
